import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { toVocative, greekGender } from '../lib/greekVocative';

/**
 * Personalised T-15 reminder banner. Renders only when a logged-in member
 * has at least one CONFIRMED booking starting within the next 15 days.
 *
 * Copy adapts to days-to-start so it reads naturally:
 *   T-0     → "Σήμερα ξεκινάς, Θεόδωρε!"          (today)
 *   T-1     → "Αύριο φεύγουμε, Θεόδωρε!"           (tomorrow)
 *   T-2…T-7 → "Έτοιμος Θεόδωρε για το σαββατο…;"   (this week / weekend variant)
 *   T-8…T-15→ "Σε X μέρες, Θεόδωρε — {trip}"       (still > a week out)
 *
 * Hidden when: user logged-out, no bookings in window, fetch fails, or
 * the user explicitly dismisses (sessionStorage flag per session).
 */
export default function UpcomingTripBanner() {
  const { user, authFetch } = useAuth();
  const { i18n } = useTranslation();
  const isGr = i18n.language === 'gr';
  const t = (en, gr) => (isGr ? gr : en);
  const [trip, setTrip] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user) { setTrip(null); return undefined; }
    const ac = new AbortController();
    authFetch('/api/bookings/my/upcoming?max_days=15', { signal: ac.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http_' + r.status))))
      .then((d) => {
        const first = (d.trips || [])[0];
        if (first && !ac.signal.aborted) setTrip(first);
      })
      .catch(() => { /* silent — banner is non-critical */ });
    return () => ac.abort();
  }, [user, authFetch]);

  // Per-booking session-scoped dismissal so a member doesn't get nagged in
  // every navigation. Resets on next session / new booking.
  useEffect(() => {
    if (!trip) return;
    const key = `tripbanner:dismissed:${trip.booking_id}`;
    if (sessionStorage.getItem(key) === '1') setDismissed(true);
  }, [trip]);

  if (!user || !trip || dismissed) return null;

  const firstName = (user.profile?.first_name || '').trim();
  const greeting = firstName ? (isGr ? toVocative(firstName) : firstName) : null;
  const tripTitle = (isGr ? (trip.title_gr || trip.title) : trip.title) || t('your trip', 'την εξόρμηση');
  const detailHref = trip.slug ? `/${trip.kind === 'class' ? 'classes' : 'expeditions'}/${trip.slug}` : '/portal';
  const d = trip.days_to_start;

  // ── headline copy variants ────────────────────────────────────────────
  // Style: when we have the member's first name, lead the sentence with it
  // (e.g. "Θεόδωρε, σήμερα είναι η μέρα!"). Falls back to neutral copy when
  // the name is missing so the banner still reads cleanly.
  let headline;
  if (d === 0) {
    headline = greeting
      ? t(`${greeting}, today's the day — see you on the trail!`, `${greeting}, σήμερα είναι η μέρα — τα λέμε στο μονοπάτι!`)
      : t("Today's the day — see you on the trail!", 'Σήμερα είναι η μέρα — τα λέμε στο μονοπάτι!');
  } else if (d === 1) {
    headline = greeting
      ? t(`${greeting}, we head out tomorrow!`, `${greeting}, αύριο φεύγουμε!`)
      : t('Tomorrow we head out!', 'Αύριο φεύγουμε!');
  } else if (d <= 7) {
    // Detect weekend departure (Greek Sat=Σ/6, Sun=Κ/0) to use the weekend variant
    let isWeekend = false;
    try {
      const dt = new Date(trip.start_date);
      const dow = dt.getDay(); // 0=Sun, 6=Sat
      isWeekend = dow === 0 || dow === 6;
    } catch { /* noop */ }
    if (isWeekend) {
      const readyAdj = greekGender(firstName) === 'f' ? 'έτοιμη' : 'έτοιμος';
      headline = greeting
        ? t(`${greeting}, ready for the weekend?`, `${greeting} ${readyAdj} για το Σαββατοκύριακο;`)
        : t('Ready for the weekend?', 'Έτοιμος/η για το Σαββατοκύριακο;');
    } else {
      headline = greeting
        ? t(`${greeting}, ${d} days to go — gear up!`, `${greeting}, σε ${d} μέρες — ετοιμασίες!`)
        : t(`${d} days to go — gear up!`, `Σε ${d} μέρες — ετοιμασίες!`);
    }
  } else {
    // T-8 … T-15
    headline = greeting
      ? t(`${greeting}, ${d} days until your next trip.`, `${greeting}, σε ${d} μέρες η επόμενη εξόρμηση.`)
      : t(`${d} days until your next trip.`, `Σε ${d} μέρες η επόμενη εξόρμηση.`);
  }

  const onDismiss = () => {
    sessionStorage.setItem(`tripbanner:dismissed:${trip.booking_id}`, '1');
    setDismissed(true);
  };

  return (
    <div
      className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-y border-amber-200"
      data-testid="upcoming-trip-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900 truncate" data-testid="upcoming-trip-banner-headline">
            {headline}
          </div>
          <Link
            to={detailHref}
            className="text-xs text-amber-800 hover:text-amber-950 truncate inline-flex items-center gap-1 mt-0.5"
            data-testid="upcoming-trip-banner-link"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span className="truncate">{tripTitle}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <button
          onClick={onDismiss}
          aria-label={t('Dismiss banner', 'Απόρριψη')}
          className="text-amber-800 hover:text-amber-950 text-xs font-medium px-2 py-1 rounded hover:bg-amber-100 transition"
          data-testid="upcoming-trip-banner-dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
